// Code from: https://reactflow.dev/examples/edges/floating-edges
import * as React from 'react';
import {
  EdgeProps,
  EdgeTextProps,
  ReactFlowState,
  Rect,
  getBezierPath,
  useStore,
} from 'reactflow';

import { useColorScheme } from '@/hooks/useColorScheme';
import {
  getBidirectionPath,
  getEdgeParams,
  getSelfLinkedPath,
} from '@/utils/graph-render-utils';
import { cn } from '@/utils/tailwind';

import { StateContext } from '../../../control/state';
import { getLoadingTasks } from '../../../data/misc';
import {
  hashString,
  hslaToStr,
  hslaWithAlpha,
  hslaWithMinLightness,
  numberToColor,
} from '../../../utils';
import { overrideReservedLabelsColor } from './graph.utils';

function EdgeText({
  x,
  y,
  label,
  labelStyle = {},
  children,
  className,
  mask,
  ...rest
}: EdgeTextProps & {
  mask: { id: string; sx: number; sy: number; tx: number; ty: number };
}) {
  const [edgeTextBbox, setEdgeTextBbox] = React.useState<Rect>({
    x: 1,
    y: 0,
    width: 0,
    height: 0,
  });
  const edgeTextClasses = cn(['react-flow__edge-textwrapper', className]);
  const edgeTextRef = React.useRef<SVGTextElement | null>(null);

  React.useEffect(() => {
    if (edgeTextRef.current) {
      const textBbox = edgeTextRef.current.getBBox();

      setEdgeTextBbox({
        x: textBbox.x,
        y: textBbox.y,
        width: textBbox.width,
        height: textBbox.height,
      });
    }
  }, [label]);

  if (typeof label === 'undefined' || !label) {
    return null;
  }

  const minLabelX = x - edgeTextBbox.width / 2;
  const minLabelY = y - edgeTextBbox.height / 2;

  const maxLabelX = x + edgeTextBbox.width / 2;
  const maxLabelY = y + edgeTextBbox.height / 2;

  const minX = Math.min(mask.sx, mask.tx, minLabelX) - 10;
  const minY = Math.min(mask.sy, mask.ty, minLabelY) - 10;

  const maxX = Math.max(mask.sx, mask.tx, maxLabelX) + 10;
  const maxY = Math.max(mask.sy, mask.ty, maxLabelY) + 10;

  return (
    <>
      <g
        transform={`translate(${x - edgeTextBbox.width / 2} ${
          y - edgeTextBbox.height / 2
        })`}
        className={edgeTextClasses}
        visibility={edgeTextBbox.width ? 'visible' : 'hidden'}
        {...rest}
      >
        <text
          className="react-flow__edge-text"
          y={edgeTextBbox.height / 2}
          dy="0.3em"
          ref={edgeTextRef}
          style={labelStyle}
        >
          {label}
        </text>
        {children}
      </g>

      <mask id={`mask_${mask.id}`} maskUnits="userSpaceOnUse">
        <rect
          x={minX}
          y={minY}
          width={maxX - minX}
          height={maxY - minY}
          fill="white"
          stroke="black"
        />

        <rect
          x={x - edgeTextBbox.width / 2 - 5}
          y={y - edgeTextBbox.height / 2 - 5}
          width={edgeTextBbox.width + 10}
          height={edgeTextBbox.height + 10}
          fill="black"
        />
      </mask>
    </>
  );
}

export function FloatingEdge({
  id,
  source,
  target,
  style,
  label,
  labelStyle,
  labelBgBorderRadius,
  labelShowBg,
  labelBgPadding,
  labelBgStyle,
  animated,
}: EdgeProps) {
  const { isDarkMode } = useColorScheme();
  const sourceNode = useStore(
    React.useCallback((store) => store.nodeInternals.get(source), [source])
  );

  const targetNode = useStore(
    React.useCallback((store) => store.nodeInternals.get(target), [target])
  );

  const state = React.useContext(StateContext);
  const isLoading = React.useMemo(
    () => getLoadingTasks(state.updates).length > 0,
    [state.updates]
  );

  const activeEdge = React.useMemo(() => {
    if (!sourceNode || !targetNode) return false;

    // graph in progress, we defer to the `animated` property
    // set in the edge, as it has the info about what edge is currently active
    if (isLoading) return animated;

    // show only edges, which are related to the focused node
    if (state.hoverSelection != null) {
      const isActive = (value: string) => {
        return (
          value === state.hoverSelection ||
          value.startsWith(state.hoverSelection + ':')
        );
      };

      return isActive(sourceNode.id) || isActive(targetNode.id);
    }

    return true;
  }, [sourceNode, targetNode, isLoading, animated, state.hoverSelection]);

  const { isBidirectional, isConditionalBidirectional } = useStore(
    (s: ReactFlowState) => {
      const currentEdge = s.edges.find(
        (e) => e.source === source && e.target === target
      );
      const bidirectionalEdge = s.edges.find(
        (e) => e.source === target && e.target === source
      );

      return {
        isBidirectional: !!bidirectionalEdge,
        isConditionalBidirectional:
          !!bidirectionalEdge &&
          !!(
            bidirectionalEdge.data.conditional || currentEdge?.data.conditional
          ),
      };
    }
  );
  const isSelfLinked = source === target;

  if (!sourceNode || !targetNode) return null;
  const sourceColor = overrideReservedLabelsColor(
    { label: sourceNode.data.label, isDarkMode },
    hslaWithMinLightness(numberToColor(hashString(sourceNode.data.label)), 60)
  );
  const targetColor = overrideReservedLabelsColor(
    { label: targetNode.data.label, isDarkMode },
    hslaWithMinLightness(numberToColor(hashString(targetNode.data.label)), 60)
  );

  const sourceEdgeColor = hslaWithAlpha(sourceColor, 0.8);
  const targetEdgeColor = hslaWithAlpha(targetColor, 0.8);

  const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(
    sourceNode,
    targetNode
  );

  const [edgePath, labelX, labelY] = isSelfLinked
    ? getSelfLinkedPath(sourceNode)
    : (isConditionalBidirectional ? getBidirectionPath : getBezierPath)({
        sourceX: sx,
        sourceY: sy,
        sourcePosition: sourcePos,
        targetPosition: targetPos,
        targetX: tx,
        targetY: ty,
      });

  return (
    <>
      <path
        key={id}
        id={id}
        className="react-flow__edge-path transition-opacity"
        d={edgePath}
        markerEnd={`url(#marker_${id})`}
        style={{
          ...style,
          opacity: activeEdge ? 1 : 0.2,
          stroke:
            isBidirectional && !isConditionalBidirectional && !isSelfLinked
              ? `url(#paint_${id})`
              : hslaToStr(sourceEdgeColor),
        }}
        mask={label ? `url(#mask_${id})` : undefined}
      />
      <EdgeText
        x={labelX}
        y={labelY}
        label={label}
        className="fill-transparent"
        labelStyle={
          labelStyle ?? {
            fill: 'hsl(var(--ls-black))',
            opacity: activeEdge ? 1 : 0.2,
            transition: 'opacity 0.3s',
          }
        }
        labelShowBg={labelShowBg ?? true}
        labelBgStyle={labelBgStyle ?? { fill: 'var(--debugger-secondary-bg)' }}
        labelBgPadding={labelBgPadding ?? [0, 0]}
        labelBgBorderRadius={labelBgBorderRadius ?? 0}
        mask={{ id, sx, sy, tx, ty }}
      />

      <defs>
        <linearGradient
          id={`paint_${id}`}
          x1={sx}
          y1={sy}
          x2={tx}
          y2={ty}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={hslaToStr(targetEdgeColor)} />
          <stop offset="1" stopColor={hslaToStr(sourceEdgeColor)} />
        </linearGradient>

        <marker
          id={`marker_${id}`}
          markerWidth="30"
          markerHeight="30"
          viewBox="-10 -10 20 20"
          markerUnits="strokeWidth"
          orient="auto-start-reverse"
          refX="0"
          refY="0"
        >
          <polyline
            strokeLinecap="round"
            strokeLinejoin="round"
            points="-5,-4 0,0 -5,4 -5,-4"
            style={{ fill: hslaToStr(sourceColor) }}
          />
        </marker>
      </defs>
    </>
  );
}
